/* eslint-disable no-restricted-globals */
import { useState, useRef } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, Snackbar, SnackbarCloseReason, Typography, Backdrop, CircularProgress, LinearProgress } from '@mui/material';
import { AutoCompleteContaBancaria, IVFormErrors, VForm, VTextFieldData, useVForm } from '../../../shared/forms';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { FaturamentoService } from '../../../shared/services/api/Financeiro/Faturamento';

interface IFormData {
  idmov: string;
  idconta: string;
  datapgto: string;
}
export interface ConfirmationDialogRawProps {
  id: string | null;
  open: boolean;
  parceiroNome: string;
  valorTotal: number;
  onClose: (value?: string) => void;
}
const formValidationSchema: yup.ObjectSchema<Omit<IFormData, 'idmov'>> =
  yup.object().shape({
    idconta: yup.string().required('Conta é obrigatória'),
    datapgto: yup.string().required('Data é obrigatória'),
  });

// Componente de Loading de Tela Cheia escuro
const FullScreenLoading: React.FC<{ message?: string; progress?: number }> = ({
  message = "Faturando...",
  progress
}) => {
  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
      open={true}
    >
      <CircularProgress
        size={60}
        sx={{
          color: 'white',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: 'white',
          fontWeight: 500,
          textAlign: 'center'
        }}
      >
        {message}
      </Typography>
      {progress !== undefined && progress > 0 && (
        <Box sx={{ width: '300px', mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, display: 'block', textAlign: 'center' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center'
        }}
      >
        Aguarde enquanto processamos sua solicitação...
      </Typography>
    </Backdrop>
  );
};

export const DialogFaturar: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
  const { onClose, open } = props;
  const { formRef, save, } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Faturando...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const requestInProgress = useRef(false);
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const simulateProgress = () => {
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);
    return () => clearInterval(interval);
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    requestInProgress.current = false;
    if (tms === 'success') {
      onClose();
    }
  };

  const handleSave = (dados: IFormData) => {
    if (requestInProgress.current) {
      return;
    }
    requestInProgress.current = true;
    let progressCleanup: (() => void) | null = null;
    formValidationSchema.validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        setShowFullScreenLoading(true);
        setLoadingMessage('Validando dados...');
        progressCleanup = simulateProgress();

        setLoadingMessage('Processando faturamento...');
        setLoadingProgress(40);

        const dadosEnvio: IFormData = {
          ...dadosValidados,
          idmov: props.id ?? '',
          datapgto: dayjs(dadosValidados.datapgto).format('YYYY-MM-DD'),
        };

        setLoadingMessage('Finalizando lançamento...');
        setLoadingProgress(70);

        return FaturamentoService
          .createfaturar(dadosEnvio)
          .then((result) => {
            setLoadingProgress(100);
            setLoadingMessage('Faturamento concluído!');

            setTimeout(() => {
              setIsLoading(false);
              setShowFullScreenLoading(false);

              if (result instanceof Error) {
                requestInProgress.current = false;

                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);

              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }

              if (progressCleanup) {
                progressCleanup();
              }
            }, 500);
          });
      })
      .catch((errors: yup.ValidationError) => {

        requestInProgress.current = false;

        setIsLoading(false);
        setShowFullScreenLoading(false);

        if (progressCleanup) {
          progressCleanup();
        }

        const validationErrors: IVFormErrors = {};

        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);

        setTms('error');
        setMsg('Erro de validação. Verifique os campos.');
        setOpenmsg(true);
      })
      .catch((error) => {

        requestInProgress.current = false;

        setIsLoading(false);
        setShowFullScreenLoading(false);

        if (progressCleanup) {
          progressCleanup();
        }

        setTms('error');
        setMsg(error.message || 'Erro ao processar faturamento');
        setOpenmsg(true);
      });
  };

  const handleCancel = () => {
    if (showFullScreenLoading) {
      return;
    }
    onClose();
  };

  return (
    <>
      {showFullScreenLoading && (
        <FullScreenLoading
          message={loadingMessage}
          progress={loadingProgress}
        />
      )}
      <Dialog
        open={open}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 1,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            pb: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Icon color="primary">payments</Icon>
            <Typography fontWeight={700} fontSize="1.1rem">
              Faturar
            </Typography>
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ lineHeight: 1.2 }}
          >
            {props.parceiroNome}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={800}
            color="success.main"
            sx={{ letterSpacing: 0.5 }}
          >
            {Number(props.valorTotal).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <Snackbar
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              open={openmsg}
              autoHideDuration={tms === 'error' ? null : 2000}
              onClose={handleCloseMsg}
            >
              <Alert
                onClose={handleCloseMsg}
                severity={tms}
                variant="filled"
                sx={{ width: '100%' }}
              >
                {msg}
              </Alert>
            </Snackbar>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AutoCompleteContaBancaria
                  name='idconta'
                  label='Conta / Caixa'
                />
              </Grid>
              <Grid item xs={12}>
                <VTextFieldData
                  fullWidth
                  name='datapgto'
                  label='Data Pagamento'
                />
              </Grid>
            </Grid>
          </VForm>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {!showFullScreenLoading && (
            <>
              <Button
                variant='text'
                color='inherit'
                onClick={handleCancel}
              >
                Cancelar
              </Button>

              <Button
                variant='contained'
                startIcon={<Icon>check</Icon>}
                onClick={save}
                disabled={isLoading || requestInProgress.current}
              >
                {isLoading ? 'Faturando...' : 'Confirmar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};